import { useEffect, useMemo, useState } from "react";
import { customerText } from "../AutoDigitizePlayground/customerText";
import "./AutoDigitizeProgress.css";

const STEPS = [
  ["trace", "Trace image"],
  ["analysis", "Plan layers"],
  ["stitches", "Build stitches"],
  ["export", "Generate DST"],
  ["save", "Save project"],
];

const formatElapsed = (seconds) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

export async function prepareAutoDigitizeImage(file) {
  if (!file || !["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
    throw new Error("Choose a PNG, JPEG or WebP image.");
  }
  if (file.size > 20 * 1024 * 1024) {
    throw new Error("Choose an image under 20 MB.");
  }

  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    const scale = Math.min(1, 1600 / Math.max(image.width, image.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(image.width * scale));
    canvas.height = Math.max(1, Math.round(image.height * scale));
    canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);
    return {
      image: canvas.toDataURL("image/png"),
      name: file.name.replace(/\.[^.]+$/, "") || "Embroidery design",
    };
  } catch (_) {
    throw new Error("This image could not be opened.");
  } finally {
    URL.revokeObjectURL(url);
  }
}

export default function AutoDigitizeProgress({
  run,
  busy,
  visible,
  onMinimize,
  onRetry,
  onClose,
}) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!busy) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [busy]);

  const elapsed = Math.max(0, Math.floor((now - (run?.startedAt || now)) / 1000));
  const steps = run?.generateDst === false
    ? STEPS.filter(([key]) => key !== "export")
    : STEPS;
  const current = run?.stage === "done"
    ? steps.length
    : Math.max(0, steps.findIndex(([key]) => key === run?.stage));
  const checkBack = useMemo(() => {
    const date = new Date((run?.startedAt || Date.now()) + 10 * 60 * 1000);
    return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }, [run?.startedAt]);

  if (!run) return null;
  if (!visible) {
    if (run.saved) return null;
    return (
      <button className="auto-digitize-pill" type="button" onClick={onMinimize}>
        <span className={busy ? "auto-digitize-spinner" : "auto-digitize-pill-check"} />
        {busy ? `Trace is working · ${formatElapsed(elapsed)}` : "Auto-digitize needs attention"}
      </button>
    );
  }

  return (
    <div className="auto-digitize-backdrop" role="dialog" aria-modal="true" aria-labelledby="auto-digitize-title">
      <section className="auto-digitize-progress-card">
        <div className="auto-digitize-progress-heading">
          <div>
            <span className="auto-digitize-kicker">AUTO-DIGITIZE · BETA</span>
            <h2 id="auto-digitize-title">
              {run.error ? "Trace needs your help" : busy ? "Trace is building your design" : "Your design is ready"}
            </h2>
          </div>
          {busy && <span className="auto-digitize-spinner auto-digitize-spinner--large" />}
        </div>

        <div className="auto-digitize-preview-row">
          {run.image ? <img src={run.image} alt="Artwork being digitized" /> : <span className="auto-digitize-preview-placeholder">✦</span>}
          <div>
            <strong>{run.name}</strong>
            <p>{customerText(run.error || run.progress || "Preparing your image…")}</p>
          </div>
        </div>

        <ol className="auto-digitize-steps">
          {steps.map(([key, label], index) => (
            <li key={key} className={index < current ? "is-done" : index === current && busy ? "is-active" : ""}>
              <span>{index < current ? "✓" : index + 1}</span>{label}
            </li>
          ))}
        </ol>

        {busy && (
          <div className="auto-digitize-time">
            <span><small>Time elapsed</small>{formatElapsed(elapsed)}</span>
            <span><small>Check back in</small>10 minutes</span>
            <span><small>Check back around</small>{checkBack}</span>
          </div>
        )}

        <div className="auto-digitize-actions">
          {run.error ? (
            <>
              {run.id && <button type="button" className="auto-digitize-primary" onClick={onRetry}>Try again</button>}
              <button type="button" className="auto-digitize-secondary" onClick={onClose}>Close</button>
            </>
          ) : busy ? (
            <>
              <button type="button" className="auto-digitize-primary" onClick={onMinimize}>Keep browsing</button>
            </>
          ) : (
            <button type="button" className="auto-digitize-primary" onClick={onClose}>Done</button>
          )}
        </div>
        {busy && <p className="auto-digitize-background-note">You can leave this open in the background. Jobs cannot be cancelled once started. The editable project opens automatically when it is ready.</p>}
      </section>
    </div>
  );
}
